import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from "react";
import * as THREE from "three";
import {
  clampLook180,
  clampPitch180,

  createFallbackPanoramaTexture,
  createHotspotObject,
  createPanoramaGeometry,
  createPanoramaSphere,
  createPanoramaVideoSource,
  createQualityController,
  DEFAULT_FOV,
  disposeHotspotGroups,
  disposePanoramaResources,
  disposeUncachedTexture,
  get180CameraYawRange,
  getInitialPixelRatio,
  getLinkedSceneUrls,
  getMaxFovFor180,
  getPanoramaRuntime,
  getPixelRatio,
  isHotspotIntersectionInteractive,
  MAX_FOV,
  MIN_FOV,
  PANORAMA_RADIUS,
  PRIORITY,
  shouldApplyLoad,
  shouldEnableAntialias,
  TOUR_PERF,
  updateHotspotSpriteAppearance,
  updateHotspotVisibility,
  vector3ToYawPitch,
  velocityOnRelease,
  yawPitchToVector3,
} from "../utils/threeHelpers";
import { is180Scene, isTextHotspot, isVideoScene, openHotspotUrl } from "../utils/tourEditorUtils";
import styles from "./PanoramaEditorViewer.module.css";

const CROSSFADE_MS = TOUR_PERF.crossfadeMs;
const DRAG_THRESHOLD_PX = 6;
const PITCH_LIMIT = THREE.MathUtils.degToRad(85);
const INERTIA_DECAY = 6.5;
const INERTIA_STOP = 0.02;
const MAX_LOOK_VEL = 6;
const HOVER_THROTTLE_MS = 32;
const DOUBLE_CLICK_DELAY_MS = 250;
const DOUBLE_CLICK_DIST_PX = 8;

// Temporary A/B experiment. Opt in with ?videoRenderDiagnostic=normal (or suppressed).
const getVideoRenderDiagnostic = () => {
  if (typeof window === "undefined") return null;
  const mode = new URLSearchParams(window.location.search).get("videoRenderDiagnostic");
  return mode === "normal" || mode === "suppressed" ? mode : null;
};

const scheduleIdle = callback => {
  if (typeof requestIdleCallback === "function") {
    return requestIdleCallback(callback, { timeout: 400 });
  }
  return setTimeout(callback, 48);
};

const cancelIdle = id => {
  if (id == null) return;
  if (typeof cancelIdleCallback === "function") cancelIdleCallback(id);
  clearTimeout(id);
};

const sceneYawRad = sceneData =>
  THREE.MathUtils.degToRad(sceneData?.defaultView?.yaw || 0);

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

const PanoramaEditorViewer = forwardRef(({
  scene,
  onSceneChange,
  transitionKey,
  editorMode = false,
  autoRotate = false,
  autoRotateSpeed = 0,
  hotspotInteraction = "navigate",
  selectedHotspotId,
  onSphereClick,
  onHotspotClick,
  onHotspotDoubleClick,
  onHotspotMove,
  onRepositionChange,
  onLoadingChange,
  tourScenes,
}, ref) => {
  const containerRef = useRef(null);
  const canvasRef = useRef(null);
  const threeRef = useRef(null);
  const lookRef = useRef({ lon: 0, lat: 0, velLon: 0, velLat: 0 });
  const autoRotateDirRef = useRef(1);
  const pointersRef = useRef(new Map());
  const pointerMetaRef = useRef({
    dragId: null,
    didDrag: false,
    didPinch: false,
    lastMoveAt: 0,
    hoverAt: 0,
    pinch: null,
  });
  const fadeRef = useRef({ active: false, start: 0, duration: CROSSFADE_MS });
  const loadGenRef = useRef(0);
  const hoveredSpriteRef = useRef(null);
  const mountedRef = useRef(true);
  const displayedSceneRef = useRef(null);
  const pendingSceneRef = useRef(null);
  const resetLookOnLoadRef = useRef(false);
  const idleWarmRef = useRef(null);
  const scheduleWarmRef = useRef(null);
  const ownerIdRef = useRef(`pano-${Math.random().toString(36).slice(2, 10)}`);
  const cssSizeRef = useRef({ w: 1, h: 1 });
  const hiddenRef = useRef(false);
  const reducedMotionRef = useRef(prefersReducedMotion());
  const linkedUrlsRef = useRef([]);
  const assignedUrlsRef = useRef({ active: null, idle: null });
  const abortRetryRef = useRef(0);
  const fallbackRef = useRef(null);

  const sceneRef = useRef(scene);
  const tourScenesRef = useRef(tourScenes);
  const onSceneChangeRef = useRef(onSceneChange);
  const onSphereClickRef = useRef(onSphereClick);
  const onHotspotClickRef = useRef(onHotspotClick);
  const onHotspotDoubleClickRef = useRef(onHotspotDoubleClick);
  const onHotspotMoveRef = useRef(onHotspotMove);
  const onRepositionChangeRef = useRef(onRepositionChange);
  const onLoadingChangeRef = useRef(onLoadingChange);
  const hotspotInteractionRef = useRef(hotspotInteraction);
  const selectedHotspotIdRef = useRef(selectedHotspotId);
  const editorModeRef = useRef(editorMode);
  const autoRotateRef = useRef(autoRotate);
  const autoRotateSpeedRef = useRef(autoRotateSpeed);
  sceneRef.current = scene;
  tourScenesRef.current = tourScenes;
  onSceneChangeRef.current = onSceneChange;
  onSphereClickRef.current = onSphereClick;
  onHotspotClickRef.current = onHotspotClick;
  onHotspotDoubleClickRef.current = onHotspotDoubleClick;
  onHotspotMoveRef.current = onHotspotMove;
  onRepositionChangeRef.current = onRepositionChange;
  onLoadingChangeRef.current = onLoadingChange;
  hotspotInteractionRef.current = hotspotInteraction;
  selectedHotspotIdRef.current = selectedHotspotId;
  editorModeRef.current = editorMode;
  autoRotateRef.current = autoRotate;
  autoRotateSpeedRef.current = autoRotateSpeed;

  const hotspotDragRef = useRef(null);
  const clickTimerRef = useRef(null);
  const lastClickRef = useRef({ hotspotId: null, time: 0, x: 0, y: 0 });
  const cancelledByRightClickRef = useRef(false);

  const activeVideoRef = useRef(null);
  const pendingVideoRef = useRef(null);
  const wasPlayingBeforeHideRef = useRef(false);
  const wasPlayingBeforePlacementRef = useRef(false);
  const wasPlayingBeforeDragRef = useRef(false);

  const [canvasReady, setCanvasReady] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [isVideo, setIsVideo] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isBuffering, setIsBuffering] = useState(false);
  const [autoplayBlocked, setAutoplayBlocked] = useState(false);
  const [videoRenderDiagnostic, setVideoRenderDiagnostic] = useState(getVideoRenderDiagnostic);
  const suppressVideoRenderRef = useRef(videoRenderDiagnostic === "suppressed");

  const handleToggleVideoRenderDiagnostic = () => {
    const suppress = !suppressVideoRenderRef.current;
    const mode = suppress ? "suppressed" : "normal";
    // Update only the URL and render gate: keep the existing video/decoder playing.
    const url = new URL(window.location.href);
    url.searchParams.set("videoRenderDiagnostic", mode);
    window.history.replaceState(window.history.state, "", url);
    suppressVideoRenderRef.current = suppress;
    setVideoRenderDiagnostic(mode);
    threeRef.current?.requestRender?.();
  };

  const handleTogglePlay = useCallback(e => {
    e?.stopPropagation?.();
    const videoCtrl = activeVideoRef.current;
    if (!videoCtrl) return;
    if (videoCtrl.isPaused()) {
      videoCtrl.play().then(ok => {
        if (ok) {
          setIsPlaying(true);
          setAutoplayBlocked(false);
        }
      });
    } else {
      videoCtrl.pause();
      setIsPlaying(false);
    }
  }, []);

  const handleToggleMute = useCallback(e => {
    e?.stopPropagation?.();
    const videoCtrl = activeVideoRef.current;
    if (!videoCtrl) return;
    const nextMuted = !videoCtrl.isMuted();
    videoCtrl.setMuted(nextMuted);
    setIsMuted(nextMuted);
  }, []);

  const raycaster = useRef(new THREE.Raycaster());
  const mouse = useRef(new THREE.Vector2());
  const runtime = getPanoramaRuntime();

  const applyCameraRotation = useCallback(camera => {
    const { lon, lat } = lookRef.current;
    camera.lookAt(
      Math.sin(lon) * Math.cos(lat),
      Math.sin(lat),
      -Math.cos(lon) * Math.cos(lat)
    );
  }, []);

  const apply180StaticCameraBounds = useCallback((camera, centerOffset = 0) => {
    if (!camera) return;
    const maxFov = Math.min(MAX_FOV, getMaxFovFor180(camera.aspect));
    if (camera.fov > maxFov || camera.fov < MIN_FOV) {
      camera.fov = THREE.MathUtils.clamp(camera.fov, MIN_FOV, maxFov);
      camera.updateProjectionMatrix();
    }
    if (Math.abs(lookRef.current.lon - centerOffset) > Math.PI / 2) {
      lookRef.current.lon = centerOffset;
    }
    const clamped = clampLook180(
      lookRef.current.lon,
      lookRef.current.lat,
      camera,
      centerOffset,
      { mode: "static" }
    );
    lookRef.current.lon = clamped.lon;
    lookRef.current.lat = clamped.lat;
    if (clamped.clampedLon) lookRef.current.velLon = 0;
    if (clamped.clampedLat) lookRef.current.velLat = 0;
  }, []);

  const snapToDefaultLook = useCallback((sceneData = sceneRef.current) => {
    const ctx = threeRef.current;
    const view = sceneData?.defaultView;
    const is180 = is180Scene(sceneData);
    const centerOffset = is180 ? -sceneYawRad(sceneData) : 0;
    lookRef.current.lon = centerOffset;
    lookRef.current.lat = THREE.MathUtils.clamp(
      THREE.MathUtils.degToRad(view?.pitch || 0),
      -PITCH_LIMIT,
      PITCH_LIMIT
    );
    lookRef.current.velLon = 0;
    lookRef.current.velLat = 0;
    if (!ctx?.camera) return;
    const targetFov = view?.fov ?? DEFAULT_FOV;
    const maxFov = is180 ? Math.min(MAX_FOV, getMaxFovFor180(ctx.camera.aspect)) : MAX_FOV;
    ctx.camera.fov = THREE.MathUtils.clamp(targetFov, MIN_FOV, maxFov);
    ctx.camera.updateProjectionMatrix();
    if (is180) {
      apply180StaticCameraBounds(ctx.camera, centerOffset);
    }
    applyCameraRotation(ctx.camera);
    ctx.requestRender?.();
  }, [apply180StaticCameraBounds, applyCameraRotation]);

  const requestViewerRender = useCallback(() => {
    threeRef.current?.requestRender?.();
  }, []);

  const updateSpriteAppearance = useCallback((sprite, hovered = false) => {
    if (!sprite) return;
    const isSelected = Boolean(
      selectedHotspotIdRef.current &&
      sprite.userData.hotspot?.id === selectedHotspotIdRef.current
    );
    updateHotspotSpriteAppearance(sprite, {
      hovered,
      selected: isSelected,
      pulse: 0,
    });
    sprite.renderOrder = isSelected ? 20 : 10;
  }, []);

  const setHoveredSprite = useCallback(sprite => {
    const prev = hoveredSpriteRef.current;
    if (prev === sprite) return;
    if (prev) updateSpriteAppearance(prev, false);
    hoveredSpriteRef.current = sprite;
    if (sprite) updateSpriteAppearance(sprite, true);
    requestViewerRender();
  }, [requestViewerRender, updateSpriteAppearance]);

  useImperativeHandle(ref, () => ({
    resetCamera: () => {
      const displayed = displayedSceneRef.current;
      const latest = sceneRef.current;
      snapToDefaultLook(displayed && latest?.id !== displayed.id ? displayed : latest);
    },
    requestDefaultLook: () => {
      resetLookOnLoadRef.current = true;
    },
    preserveLook: () => {
      resetLookOnLoadRef.current = false;
    },
    cancelReposition: () => {
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
      if (hotspotDragRef.current) {
        const { sprite, originalCoords } = hotspotDragRef.current;
        if (sprite && originalCoords) {
          const pos = yawPitchToVector3(originalCoords.yaw, originalCoords.pitch, PANORAMA_RADIUS - 2);
          sprite.position.copy(pos);
        }
        hotspotDragRef.current = null;
        if (canvasRef.current) {
          canvasRef.current.classList.remove(styles.dragging);
          canvasRef.current.style.cursor = editorModeRef.current ? "crosshair" : "grab";
        }
        threeRef.current?.requestRender?.();
      }
    },
    prefetchIntent: imageUrl => {
      if (!imageUrl) return;
      runtime.setInterest({
        destinationUrl:
          pendingSceneRef.current?.imageUrl || displayedSceneRef.current?.imageUrl,
        linkedUrls: linkedUrlsRef.current,
        intentUrls: [imageUrl],
        ownerId: ownerIdRef.current,
      });
    },
    playVideo: () => activeVideoRef.current?.play(),
    pauseVideo: () => activeVideoRef.current?.pause(),
    setVideoMuted: muted => {
      activeVideoRef.current?.setMuted(muted);
      setIsMuted(muted);
    },
    isVideoPlaying: () => Boolean(activeVideoRef.current && !activeVideoRef.current.isPaused()),
  }), [runtime, snapToDefaultLook]);

  const rebuildHotspots = useCallback(sceneData => {
    const ctx = threeRef.current;
    if (!ctx?.hotspotRoot) return;

    if (hotspotDragRef.current) {
      const { sprite, originalCoords } = hotspotDragRef.current;
      if (sprite && originalCoords) {
        const pos = yawPitchToVector3(originalCoords.yaw, originalCoords.pitch, PANORAMA_RADIUS - 2);
        sprite.position.copy(pos);
      }
      hotspotDragRef.current = null;
    }

    disposeHotspotGroups(ctx.hotspotGroups);
    ctx.hotspotGroups = [];
    ctx.hotspotPickables = [];

    const isEditor =
      hotspotInteractionRef.current === "edit" || Boolean(editorModeRef.current);
    for (const hs of sceneData?.hotspots ?? []) {
      const isSelected = Boolean(
        selectedHotspotIdRef.current && hs.id === selectedHotspotIdRef.current
      );
      const group = createHotspotObject(hs, requestViewerRender, {
        isEditor,
        isSelected,
      });
      const sprite = group.userData.sprite;
      if (sprite) {
        const isSelected = Boolean(
          selectedHotspotIdRef.current && hs.id === selectedHotspotIdRef.current
        );
        updateHotspotSpriteAppearance(sprite, {
          hovered: false,
          selected: isSelected,
          pulse: 0,
        });
        sprite.renderOrder = isSelected ? 20 : 10;
      }
      ctx.hotspotRoot.add(group);
      ctx.hotspotGroups.push(group);
      ctx.hotspotPickables.push(group.userData.sprite);
    }
    ctx.hotspotRoot.rotation.y = sceneYawRad(sceneData);
    requestViewerRender();
  }, [requestViewerRender]);

  useEffect(() => {
    const ctx = threeRef.current;
    if (!ctx?.hotspotGroups) return;
    for (const group of ctx.hotspotGroups) {
      const sprite = group.userData.sprite;
      if (!sprite) continue;
      const isSelected = Boolean(
        selectedHotspotId && sprite.userData.hotspot?.id === selectedHotspotId
      );
      updateHotspotSpriteAppearance(sprite, {
        hovered: hoveredSpriteRef.current === sprite,
        selected: isSelected,
        pulse: 0,
      });
      sprite.renderOrder = isSelected ? 20 : 10;
    }
    requestViewerRender();
  }, [selectedHotspotId, requestViewerRender]);

  const protectUrls = useCallback(() => {
    return [
      assignedUrlsRef.current.active,
      assignedUrlsRef.current.idle,
      displayedSceneRef.current?.imageUrl,
      pendingSceneRef.current?.imageUrl,
    ].filter(Boolean);
  }, []);

  const clearMeshTexture = useCallback((mesh, slot) => {
    if (!mesh) return;
    const prevUrl = assignedUrlsRef.current[slot];
    const prevMap = mesh.material.map;
    mesh.material.map = null;
    mesh.material.needsUpdate = true;
    mesh.userData.textureUrl = null;
    mesh.userData.sceneId = null;
    mesh.userData.isVideo = false;
    assignedUrlsRef.current[slot] = null;
    if (prevMap?.userData?.videoSource) {
      try {
        prevMap.userData.videoSource.dispose();
      } catch {
        /* ignore */
      }
    } else if (prevUrl) {
      runtime.release(prevUrl, ownerIdRef.current);
    }
    if (prevMap?.userData?.uncachedFallback && !prevMap?.userData?.videoSource) {
      disposeUncachedTexture(prevMap);
      if (fallbackRef.current === prevMap) fallbackRef.current = null;
    }
  }, [runtime]);

  const assignMeshTexture = useCallback((mesh, slot, texture, sceneData) => {
    const isVid = isVideoScene(sceneData);
    const nextUrl = isVid ? sceneData?.videoUrl : sceneData?.imageUrl || null;
    const prevUrl = assignedUrlsRef.current[slot];
    const prevMap = mesh.material.map;

    const ctx = threeRef.current;
    if (ctx?.geometries) {
      const is180 = is180Scene(sceneData);
      const targetGeometry = is180
        ? ctx.geometries.equirectangular_180
        : ctx.geometries.equirectangular_360;
      if (mesh.geometry !== targetGeometry) {
        mesh.geometry = targetGeometry;
      }
    }

    mesh.material.color.set(0xffffff);
    mesh.material.map = texture;
    mesh.material.needsUpdate = true;
    mesh.userData.textureUrl = nextUrl;
    mesh.userData.sceneId = sceneData?.id;
    mesh.userData.isVideo = isVid;
    mesh.rotation.y = sceneYawRad(sceneData);
    assignedUrlsRef.current[slot] = nextUrl;
    if (prevUrl && prevUrl !== nextUrl) {
      if (prevMap?.userData?.videoSource) {
        try {
          prevMap.userData.videoSource.dispose();
        } catch {
          /* ignore */
        }
      } else {
        runtime.release(prevUrl, ownerIdRef.current);
      }
    }
    if (
      prevMap &&
      prevMap !== texture &&
      prevMap.userData?.uncachedFallback &&
      !prevMap.userData?.videoSource
    ) {
      disposeUncachedTexture(prevMap);
      if (fallbackRef.current === prevMap) fallbackRef.current = null;
    }
  }, [runtime]);

  const completeCrossfade = useCallback(ctx => {
    fadeRef.current.active = false;
    const idle = ctx.idleMesh;
    const active = ctx.activeMesh;
    if (!idle?.visible || idle === active) return;

    idle.material.opacity = 1;
    idle.material.transparent = false;
    idle.material.depthTest = true;
    idle.material.depthWrite = true;
    idle.renderOrder = 0;
    idle.visible = true;

    active.visible = false;
    active.material.opacity = 1;
    active.material.transparent = false;
    active.material.depthTest = true;
    active.material.depthWrite = true;
    active.renderOrder = 0;
    clearMeshTexture(active, "active");

    ctx.activeMesh = idle;
    ctx.idleMesh = active;
    assignedUrlsRef.current.active = assignedUrlsRef.current.idle;
    assignedUrlsRef.current.idle = null;

    const incoming = pendingSceneRef.current;
    if (incoming) {
      displayedSceneRef.current = incoming;
      if (is180Scene(incoming) && ctx?.camera) {
        const centerOffset = -sceneYawRad(incoming);
        apply180StaticCameraBounds(ctx.camera, centerOffset);
        lookRef.current.velLon = 0;
      }
      rebuildHotspots(incoming);
      if (isVideoScene(incoming)) {
        setIsVideo(true);
        setIsBuffering(false);
        if (activeVideoRef.current) {
          setIsPlaying(!activeVideoRef.current.isPaused());
        }
      } else {
        setIsVideo(false);
        setIsPlaying(false);
        setAutoplayBlocked(false);
        setIsBuffering(false);
        activeVideoRef.current = null;
      }
      linkedUrlsRef.current = isVideoScene(incoming)
        ? []
        : getLinkedSceneUrls(incoming, tourScenesRef.current);
      if (incoming.imageUrl) {
        runtime.setInterest({
          destinationUrl: incoming.imageUrl,
          linkedUrls: linkedUrlsRef.current,
          ownerId: ownerIdRef.current,
        });
      }
    }
    runtime.prune(protectUrls());
    ctx.requestRender?.();
    if (!isVideoScene(incoming)) {
      scheduleWarmRef.current?.();
    }
  }, [apply180StaticCameraBounds, clearMeshTexture, protectUrls, rebuildHotspots, runtime]);

  const beginTextureSwap = useCallback((ctx, texture, sceneData, instant) => {
    if (fadeRef.current.active) completeCrossfade(ctx);

    const idle = ctx.idleMesh;
    const active = ctx.activeMesh;
    assignMeshTexture(idle, "idle", texture, sceneData);

    const hasCurrent = Boolean(active.material.map) && active.visible;
    if (instant || !hasCurrent) {
      idle.material.opacity = 1;
      idle.material.transparent = false;
      idle.material.depthTest = true;
      idle.material.depthWrite = true;
      idle.renderOrder = 0;
      idle.visible = true;
      if (active !== idle) {
        active.visible = false;
        clearMeshTexture(active, "active");
        ctx.activeMesh = idle;
        ctx.idleMesh = active;
        assignedUrlsRef.current.active = assignedUrlsRef.current.idle;
        assignedUrlsRef.current.idle = null;
      }
      fadeRef.current.active = false;
      displayedSceneRef.current = sceneData;
      if (is180Scene(sceneData) && ctx?.camera) {
        const centerOffset = -sceneYawRad(sceneData);
        apply180StaticCameraBounds(ctx.camera, centerOffset);
        lookRef.current.velLon = 0;
      }
      rebuildHotspots(sceneData);
      if (isVideoScene(sceneData)) {
        setIsVideo(true);
        setIsBuffering(false);
        if (activeVideoRef.current) {
          setIsPlaying(!activeVideoRef.current.isPaused());
        }
      } else {
        setIsVideo(false);
        setIsPlaying(false);
        setAutoplayBlocked(false);
        setIsBuffering(false);
        activeVideoRef.current = null;
      }
      linkedUrlsRef.current = isVideoScene(sceneData)
        ? []
        : getLinkedSceneUrls(sceneData, tourScenesRef.current);
      if (sceneData.imageUrl) {
        runtime.setInterest({
          destinationUrl: sceneData.imageUrl,
          linkedUrls: linkedUrlsRef.current,
          ownerId: ownerIdRef.current,
        });
      }
      return;
    }

    idle.material.transparent = true;
    idle.material.opacity = 0;
    idle.material.depthTest = false;
    idle.material.depthWrite = false;
    idle.renderOrder = 1;
    idle.visible = true;
    active.renderOrder = 0;
    if (is180Scene(sceneData) && ctx?.camera) {
      const centerOffset = -sceneYawRad(sceneData);
      apply180StaticCameraBounds(ctx.camera, centerOffset);
      lookRef.current.velLon = 0;
    }
    fadeRef.current = {
      active: true,
      start: performance.now(),
      duration: CROSSFADE_MS,
    };
  }, [apply180StaticCameraBounds, assignMeshTexture, clearMeshTexture, completeCrossfade, rebuildHotspots, runtime]);

  const tickCrossfade = useCallback((ctx, now) => {
    const fade = fadeRef.current;
    if (!fade.active) return false;
    const t = Math.min(1, (now - fade.start) / fade.duration);
    const k = t * t * (3 - 2 * t);
    ctx.idleMesh.material.opacity = k;
    if (t >= 1) completeCrossfade(ctx);
    return fade.active || t < 1;
  }, [completeCrossfade]);

  const scheduleWarm = useCallback(() => {
    cancelIdle(idleWarmRef.current);
    const ctx = threeRef.current;
    if (!ctx?.renderer || hiddenRef.current) return;
    if (activeVideoRef.current && !activeVideoRef.current.isPaused()) return;
    idleWarmRef.current = scheduleIdle(() => {
      const current = threeRef.current;
      const meta = pointerMetaRef.current;
      if (!current?.renderer || hiddenRef.current) return;
      if (meta.dragId != null || meta.pinch || fadeRef.current.active) return;
      if (activeVideoRef.current && !activeVideoRef.current.isPaused()) return;
      const candidate = runtime.nextWarmCandidate(
        current.rendererId,
        linkedUrlsRef.current
      );
      if (!candidate) return;
      if (suppressVideoRenderRef.current && candidate.texture.isVideoTexture) return;
      try {
        current.renderer.initTexture(candidate.texture);
        runtime.markWarmed(candidate.url, current.rendererId);
      } catch {
        /* context not ready */
      }
      if (runtime.nextWarmCandidate(current.rendererId, linkedUrlsRef.current)) {
        scheduleWarm();
      }
    });
  }, [runtime]);

  scheduleWarmRef.current = scheduleWarm;

  const loadPanoramaScene = useCallback(
    async sceneData => {
      const gen = ++loadGenRef.current;
      const ctx = threeRef.current;
      if (!ctx || !sceneData) return;

      pendingSceneRef.current = sceneData;
      setLoadError(null);
      const isFirstPaint = !ctx.activeMesh?.material?.map;
      if (isFirstPaint) onLoadingChangeRef.current?.(true);

      const isVid = isVideoScene(sceneData);

      if (isVid) {
        setIsVideo(true);
        setIsBuffering(true);
        setAutoplayBlocked(false);

        // Pause existing playing video immediately so outgoing video does not
        // decode 4K in parallel while the incoming video is loading/buffering
        if (activeVideoRef.current && !activeVideoRef.current.isPaused()) {
          try {
            activeVideoRef.current.pause();
          } catch {
            /* ignore */
          }
        }

        if (pendingVideoRef.current) {
          try {
            pendingVideoRef.current.dispose();
          } catch {
            /* ignore */
          }
          pendingVideoRef.current = null;
        }

        linkedUrlsRef.current = [];
        if (sceneData.imageUrl) {
          runtime.setInterest({
            destinationUrl: sceneData.imageUrl,
            linkedUrls: [],
            ownerId: ownerIdRef.current,
          });
        }

        let videoSource = null;
        try {
          videoSource = createPanoramaVideoSource(sceneData.videoUrl, {
            onFrame: () => {
              threeRef.current?.requestRender?.();
            },
            onWaiting: buffering => {
              if (gen === loadGenRef.current) setIsBuffering(buffering);
            },
            onPlaying: () => {
              if (gen === loadGenRef.current) {
                setIsPlaying(true);
                setIsBuffering(false);
                setAutoplayBlocked(false);
              }
            },
            onError: () => {
              if (gen === loadGenRef.current) {
                setIsBuffering(false);
              }
            },
            loop: true,
            muted: isMuted,
          });
          pendingVideoRef.current = videoSource;

          await videoSource.waitForFirstFrame();

          if (
            !shouldApplyLoad(gen, loadGenRef.current, mountedRef.current) ||
            !threeRef.current
          ) {
            videoSource.dispose();
            return;
          }

          pendingVideoRef.current = null;

          if (resetLookOnLoadRef.current || isFirstPaint) {
            resetLookOnLoadRef.current = false;
            snapToDefaultLook(sceneData);
          }

          abortRetryRef.current = 0;
          beginTextureSwap(threeRef.current, videoSource.texture, sceneData, isFirstPaint);
          activeVideoRef.current = videoSource;

          const playSuccess = await videoSource.play();
          if (gen === loadGenRef.current && mountedRef.current) {
            setIsPlaying(playSuccess);
            setAutoplayBlocked(!playSuccess);
            const stillBuffering = videoSource.isBuffering ? videoSource.isBuffering() : false;
            setIsBuffering(stillBuffering);
            onLoadingChangeRef.current?.(false);
          }

          threeRef.current.requestRender?.();
        } catch (err) {
          if (pendingVideoRef.current === videoSource) {
            pendingVideoRef.current = null;
          }
          videoSource?.dispose?.();

          if (!shouldApplyLoad(gen, loadGenRef.current, mountedRef.current)) return;

          console.warn(
            "[360 Tour] Video stream load failed, attempting poster fallback:",
            sceneData?.id,
            err
          );

          try {
            let posterTexture = null;
            if (sceneData.imageUrl) {
              posterTexture = await runtime.request(sceneData.imageUrl, {
                ownerId: ownerIdRef.current,
                priority: PRIORITY.ACTIVE,
                speculative: false,
              });
            }
            if (
              !shouldApplyLoad(gen, loadGenRef.current, mountedRef.current) ||
              !threeRef.current
            ) {
              if (posterTexture && sceneData.imageUrl) {
                runtime.release(sceneData.imageUrl, ownerIdRef.current);
              }
              return;
            }
            if (posterTexture) {
              beginTextureSwap(threeRef.current, posterTexture, sceneData, true);
            } else {
              const fallback = createFallbackPanoramaTexture(sceneData.id, sceneData.name);
              fallbackRef.current = fallback;
              beginTextureSwap(threeRef.current, fallback, sceneData, true);
            }
          } catch {
            const fallback = createFallbackPanoramaTexture(sceneData.id, sceneData.name);
            fallbackRef.current = fallback;
            beginTextureSwap(threeRef.current, fallback, sceneData, true);
          }

          setLoadError({ sceneId: sceneData.id, videoUrl: sceneData.videoUrl, isVideo: true });
          setIsBuffering(false);
          onLoadingChangeRef.current?.(false);
          threeRef.current?.requestRender?.();
        }
        return;
      }

      // --- Standard Image Scene Loading Path ---
      setIsVideo(false);
      setIsPlaying(false);
      setAutoplayBlocked(false);
      setIsBuffering(false);
      if (activeVideoRef.current) {
        try {
          activeVideoRef.current.pause();
        } catch {
          /* ignore */
        }
      }
      if (pendingVideoRef.current) {
        try {
          pendingVideoRef.current.dispose();
        } catch {
          /* ignore */
        }
        pendingVideoRef.current = null;
      }

      linkedUrlsRef.current = getLinkedSceneUrls(sceneData, tourScenesRef.current);
      runtime.setInterest({
        destinationUrl: sceneData.imageUrl,
        linkedUrls: linkedUrlsRef.current,
        ownerId: ownerIdRef.current,
      });

      try {
        const texture = await runtime.request(sceneData.imageUrl, {
          ownerId: ownerIdRef.current,
          priority: PRIORITY.ACTIVE,
          speculative: false,
        });
        if (!shouldApplyLoad(gen, loadGenRef.current, mountedRef.current)) {
          runtime.release(sceneData.imageUrl, ownerIdRef.current);
          return;
        }
        if (!threeRef.current) {
          runtime.release(sceneData.imageUrl, ownerIdRef.current);
          return;
        }

        if (resetLookOnLoadRef.current || isFirstPaint) {
          resetLookOnLoadRef.current = false;
          snapToDefaultLook(sceneData);
        }

        abortRetryRef.current = 0;
        beginTextureSwap(threeRef.current, texture, sceneData, isFirstPaint);
        runtime.prune(protectUrls());
        threeRef.current.requestRender?.();
        scheduleWarm();
        if (gen === loadGenRef.current) {
          onLoadingChangeRef.current?.(false);
        }
      } catch (err) {
        const aborted = runtime.isAbortError?.(err) || err?.name === "AbortError";
        if (aborted) {
          const canRetry =
            mountedRef.current &&
            gen === loadGenRef.current &&
            !threeRef.current?.activeMesh?.material?.map &&
            abortRetryRef.current < 2 &&
            sceneRef.current?.imageUrl;
          if (canRetry) {
            abortRetryRef.current += 1;
            queueMicrotask(() => {
              if (mountedRef.current && sceneRef.current) {
                loadPanoramaScene(sceneRef.current);
              }
            });
          }
          return;
        }
        console.error("[360 Tour] Panorama load failed:", sceneData?.id, err);
        if (!shouldApplyLoad(gen, loadGenRef.current, mountedRef.current)) return;
        const hasCurrent = Boolean(threeRef.current?.activeMesh?.material?.map);
        if (hasCurrent) {
          setLoadError({ sceneId: sceneData.id, imageUrl: sceneData.imageUrl });
          onLoadingChangeRef.current?.(false);
        } else {
          const fallback = createFallbackPanoramaTexture(sceneData.id, sceneData.name);
          fallbackRef.current = fallback;
          beginTextureSwap(threeRef.current, fallback, sceneData, true);
          threeRef.current?.requestRender?.();
          onLoadingChangeRef.current?.(false);
        }
      }
    },
    [beginTextureSwap, isMuted, protectUrls, runtime, scheduleWarm, snapToDefaultLook]
  );

  const handleRetry = useCallback(() => {
    const target = pendingSceneRef.current || sceneRef.current;
    if (target) loadPanoramaScene(target);
  }, [loadPanoramaScene]);

  const getPointerNDC = useCallback((clientX, clientY) => {
    const { w, h, left, top } = cssSizeRef.current;
    if (!w || !h) return null;
    mouse.current.x = ((clientX - left) / w) * 2 - 1;
    mouse.current.y = -((clientY - top) / h) * 2 + 1;
    return mouse.current;
  }, []);

  const raycastHotspot = useCallback(
    (clientX, clientY) => {
      const ctx = threeRef.current;
      if (!ctx || !getPointerNDC(clientX, clientY)) return null;
      raycaster.current.setFromCamera(mouse.current, ctx.camera);
      const hits = raycaster.current.intersectObjects(ctx.hotspotPickables, false);
      const preciseHit = hits.find(isHotspotIntersectionInteractive);
      return preciseHit?.object ?? null;
    },
    [getPointerNDC]
  );

  const raycastSphereYawPitch = useCallback(
    (clientX, clientY) => {
      const ctx = threeRef.current;
      if (!ctx || !ctx.activeMesh || !getPointerNDC(clientX, clientY)) return null;
      raycaster.current.setFromCamera(mouse.current, ctx.camera);
      const sphereHits = raycaster.current.intersectObject(ctx.activeMesh, false);
      if (!sphereHits.length) return null;
      const point = sphereHits[0].point.clone();
      ctx.activeMesh.worldToLocal(point);
      const coords = vector3ToYawPitch(point, PANORAMA_RADIUS);
      if (is180Scene(displayedSceneRef.current)) {
        coords.yaw = THREE.MathUtils.clamp(coords.yaw, -Math.PI / 2, Math.PI / 2);
      }
      return coords;
    },
    [getPointerNDC]
  );

  useLayoutEffect(() => {
    mountedRef.current = true;
    const container = containerRef.current;
    if (!container) return undefined;

    let renderer;
    let resizeObserver;
    const ownerId = ownerIdRef.current;
    try {
      const scene3d = new THREE.Scene();
      scene3d.background = new THREE.Color(0x000000);
      const w = Math.max(container.clientWidth, 1);
      const h = Math.max(container.clientHeight, 1);
      cssSizeRef.current = {
        w,
        h,
        left: 0,
        top: 0,
      };

      const camera = new THREE.PerspectiveCamera(DEFAULT_FOV, w / h, 0.1, 1000);
      camera.position.set(0, 0, 0);

      const nativeDpr =
        typeof window !== "undefined" ? window.devicePixelRatio || 1 : 1;
      const initialRatio = getPixelRatio();
      renderer = new THREE.WebGLRenderer({
        antialias: shouldEnableAntialias(nativeDpr),
        alpha: false,
        powerPreference: "high-performance",
        stencil: false,
        depth: true,
      });
      renderer.setPixelRatio(initialRatio);
      renderer.setSize(w, h, false);
      renderer.setClearColor(0x000000, 1);
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.toneMapping = THREE.NoToneMapping;
      renderer.autoClear = true;

      const canvas = renderer.domElement;
      canvas.className = styles.canvas;
      canvas.setAttribute(
        "aria-label",
        `360 panorama: ${sceneRef.current?.name ?? "preview"}`
      );
      container.appendChild(canvas);
      canvasRef.current = canvas;
      setCanvasReady(true);

      const geometry360 = createPanoramaGeometry("equirectangular_360");
      const geometry180 = createPanoramaGeometry("equirectangular_180");
      const geometries = {
        equirectangular_360: geometry360,
        equirectangular_180: geometry180,
      };

      const { mesh: meshA, material: materialA } = createPanoramaSphere(geometry360);
      const { mesh: meshB, material: materialB } = createPanoramaSphere(geometry360);
      meshB.visible = false;

      const hotspotRoot = new THREE.Group();
      scene3d.add(meshA);
      scene3d.add(meshB);
      scene3d.add(hotspotRoot);

      const quality = createQualityController({
        initialRatio,
        minRatio: TOUR_PERF.minPixelRatio,
        maxRatio: getInitialPixelRatio({
          devicePixelRatio: nativeDpr,
          deviceMemory:
            typeof navigator !== "undefined" ? navigator.deviceMemory : undefined,
        }),
      });

      const ctx = {
        scene3d,
        camera,
        renderer,
        rendererId: `gl-${Math.random().toString(36).slice(2, 9)}`,
        mesh: meshA,
        geometry: geometry360,
        geometries,
        material: materialA,
        materialA,
        materialB,
        activeMesh: meshA,
        idleMesh: meshB,
        hotspotRoot,
        hotspotGroups: [],
        hotspotPickables: [],
        running: false,
        needsRender: true,
        quality,
      };
      threeRef.current = ctx;

      let lastT = performance.now();

      const syncCssBox = () => {
        const rect = container.getBoundingClientRect();
        cssSizeRef.current = {
          w: Math.max(rect.width, 1),
          h: Math.max(rect.height, 1),
          left: rect.left,
          top: rect.top,
        };
      };
      syncCssBox();

      const applySize = () => {
        const { w: rw, h: rh } = cssSizeRef.current;
        ctx.camera.aspect = rw / rh;
        const currentScene = displayedSceneRef.current;
        if (is180Scene(currentScene)) {
          const maxFov = getMaxFovFor180(ctx.camera.aspect);
          if (ctx.camera.fov > maxFov) {
            ctx.camera.fov = maxFov;
          }
          const centerOffset = -sceneYawRad(currentScene);
          apply180StaticCameraBounds(ctx.camera, centerOffset);
        }
        ctx.camera.updateProjectionMatrix();
        ctx.renderer.setPixelRatio(ctx.quality.getRatio());
        ctx.renderer.setSize(rw, rh, false);
        ctx.needsRender = true;
      };

      const stopLoop = () => {
        ctx.running = false;
        renderer.setAnimationLoop(null);
      };

      const stepLook = dt => {
        const look = lookRef.current;
        const meta = pointerMetaRef.current;
        if (meta.dragId != null && meta.didDrag && !meta.pinch) return false;

        const currentScene = displayedSceneRef.current;
        const is180 = is180Scene(currentScene);

        if (Math.abs(look.velLon) > INERTIA_STOP || Math.abs(look.velLat) > INERTIA_STOP) {
          look.lon += look.velLon * dt;
          look.lat = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, look.lat + look.velLat * dt));

          if (is180) {
            const centerOffset = -sceneYawRad(currentScene);
            const prevLat180 = look.lat;
            look.lat = clampPitch180(look.lat, ctx.camera);
            if (Math.abs(look.lat - prevLat180) > 1e-6) {
              look.velLat = 0;
            }
            const { min, max } = get180CameraYawRange(ctx.camera, centerOffset, look.lat);
            if (look.lon <= min) {
              look.lon = min;
              look.velLon = 0;
            } else if (look.lon >= max) {
              look.lon = max;
              look.velLon = 0;
            }
          }

          const damp = Math.exp(-INERTIA_DECAY * dt);
          look.velLon *= damp;
          look.velLat *= damp;
          return true;
        }
        look.velLon = 0;
        look.velLat = 0;
        if (autoRotateRef.current && autoRotateSpeedRef.current) {
          if (is180) {
            const centerOffset = -sceneYawRad(currentScene);
            const prevLat180 = look.lat;
            look.lat = clampPitch180(look.lat, ctx.camera);
            if (Math.abs(look.lat - prevLat180) > 1e-6) {
              look.velLat = 0;
            }
            const { min, max } = get180CameraYawRange(ctx.camera, centerOffset, look.lat);
            if (min >= max) {
              look.lon = centerOffset;
              return false;
            }
            const speed = Math.abs(autoRotateSpeedRef.current) * autoRotateDirRef.current;
            look.lon += speed * dt;
            if (look.lon >= max) {
              look.lon = max;
              autoRotateDirRef.current = -1;
            } else if (look.lon <= min) {
              look.lon = min;
              autoRotateDirRef.current = 1;
            }
            return true;
          }
          look.lon += autoRotateSpeedRef.current * dt;
          return true;
        }
        return false;
      };

      const animate = () => {
        if (!threeRef.current || hiddenRef.current) {
          stopLoop();
          return;
        }
        const now = performance.now();
        const dt = Math.min(0.05, (now - lastT) / 1000);
        const dtMs = now - lastT;
        lastT = now;
        const meta = pointerMetaRef.current;
        const interacting =
          meta.dragId != null ||
          Boolean(meta.pinch) ||
          fadeRef.current.active ||
          autoRotateRef.current ||
          Math.abs(lookRef.current.velLon) > INERTIA_STOP ||
          Math.abs(lookRef.current.velLat) > INERTIA_STOP;

        const moving = stepLook(dt);
        const fading = tickCrossfade(ctx, now);
        applyCameraRotation(ctx.camera);

        if (ctx.hotspotGroups.length) {
          const is180 = is180Scene(displayedSceneRef.current);
          updateHotspotVisibility(ctx.hotspotGroups, ctx.camera, is180);
          const pulse = reducedMotionRef.current ? 0 : 0;
          for (const group of ctx.hotspotGroups) {
            const sprite = group.userData.sprite;
            if (!sprite || !group.visible) continue;
            const isSelected = Boolean(
              selectedHotspotIdRef.current &&
              sprite.userData.hotspot?.id === selectedHotspotIdRef.current
            );
            updateHotspotSpriteAppearance(sprite, {
              hovered: hoveredSpriteRef.current === sprite,
              selected: isSelected,
              pulse,
            });
          }
        }

        const nextRatio = ctx.quality.sample(dtMs, { interacting });
        if (nextRatio != null) applySize();

        // Gate the actual draw, not just onFrame: dragging, fades, resize and
        // other render requests must not consume/upload either video texture.
        // Keep callbacks and logical animation/lifecycle work unchanged.
        const suppressVideoDraw = suppressVideoRenderRef.current && (
          ctx.activeMesh?.material?.map?.isVideoTexture ||
          ctx.idleMesh?.material?.map?.isVideoTexture
        );
        if (!suppressVideoDraw) ctx.renderer.render(ctx.scene3d, ctx.camera);
        ctx.needsRender = false;

        const waitingForPanorama = !ctx.activeMesh?.material?.map;
        const keep =
          interacting ||
          moving ||
          fading ||
          waitingForPanorama ||
          ctx.needsRender;
        if (!keep) stopLoop();
      };

      ctx.requestRender = () => {
        if (hiddenRef.current || !threeRef.current) return;
        ctx.needsRender = true;
        if (ctx.running) return;
        ctx.running = true;
        lastT = performance.now();
        renderer.setAnimationLoop(animate);
      };

      if (sceneRef.current) loadPanoramaScene(sceneRef.current);
      ctx.requestRender();

      const onResize = () => {
        syncCssBox();
        applySize();
        ctx.requestRender();
      };
      window.addEventListener("resize", onResize);
      window.visualViewport?.addEventListener("resize", onResize);
      if (typeof ResizeObserver === "function") {
        resizeObserver = new ResizeObserver(onResize);
        resizeObserver.observe(container);
      }

      const onVisibility = () => {
        hiddenRef.current = document.hidden;
        runtime.setPaused(document.hidden);
        if (document.hidden) {
          if (activeVideoRef.current && !activeVideoRef.current.isPaused()) {
            wasPlayingBeforeHideRef.current = true;
            activeVideoRef.current.pause();
            setIsPlaying(false);
          } else {
            wasPlayingBeforeHideRef.current = false;
          }
          stopLoop();
          return;
        }
        if (wasPlayingBeforeHideRef.current && activeVideoRef.current) {
          wasPlayingBeforeHideRef.current = false;
          activeVideoRef.current.play().then(ok => {
            if (ok) setIsPlaying(true);
          });
        }
        lastT = performance.now();
        ctx.quality.resetTiming();
        lookRef.current.velLon = 0;
        lookRef.current.velLat = 0;
        ctx.requestRender();
        scheduleWarm();
      };
      document.addEventListener("visibilitychange", onVisibility);

      const onContextLost = event => {
        event.preventDefault();
        ctx.contextLost = true;
        runtime.clearWarming(ctx.rendererId);
        stopLoop();
      };
      const onContextRestored = () => {
        ctx.contextLost = false;
        runtime.clearWarming(ctx.rendererId);
        const map = ctx.activeMesh?.material?.map;
        if (map && !(suppressVideoRenderRef.current && map.isVideoTexture)) {
          map.needsUpdate = true;
          try {
            renderer.initTexture(map);
            if (assignedUrlsRef.current.active) {
              runtime.markWarmed(assignedUrlsRef.current.active, ctx.rendererId);
            }
          } catch {
            /* ignore */
          }
        }
        applySize();
        applyCameraRotation(ctx.camera);
        ctx.requestRender();
      };
      canvas.addEventListener("webglcontextlost", onContextLost);
      canvas.addEventListener("webglcontextrestored", onContextRestored);

      return () => {
        mountedRef.current = false;
        loadGenRef.current += 1;
        cancelIdle(idleWarmRef.current);
        hotspotDragRef.current = null;
        runtime.releaseOwner(ownerId);
        runtime.setPaused(false);
        stopLoop();
        window.removeEventListener("resize", onResize);
        window.visualViewport?.removeEventListener("resize", onResize);
        document.removeEventListener("visibilitychange", onVisibility);
        canvas.removeEventListener("webglcontextlost", onContextLost);
        canvas.removeEventListener("webglcontextrestored", onContextRestored);
        resizeObserver?.disconnect();
        if (activeVideoRef.current) {
          try {
            activeVideoRef.current.dispose();
          } catch {
            /* ignore */
          }
          activeVideoRef.current = null;
        }
        if (pendingVideoRef.current) {
          try {
            pendingVideoRef.current.dispose();
          } catch {
            /* ignore */
          }
          pendingVideoRef.current = null;
        }
        if (threeRef.current) disposeHotspotGroups(threeRef.current.hotspotGroups);
        if (fallbackRef.current) {
          disposeUncachedTexture(fallbackRef.current);
          fallbackRef.current = null;
        }
        ctx.activeMesh.material.map = null;
        ctx.idleMesh.material.map = null;
        ctx.geometries.equirectangular_360.dispose();
        ctx.geometries.equirectangular_180.dispose();
        materialA.dispose();
        materialB.dispose();
        renderer.dispose();
        if (canvas.parentNode === container) container.removeChild(canvas);
        canvasRef.current = null;
        threeRef.current = null;
        setCanvasReady(false);
      };
    } catch (err) {
      console.error("[360 Tour] WebGL init failed:", err);
      return undefined;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadedSceneIdRef = useRef(scene?.id);
  const loadedImageUrlRef = useRef(scene?.imageUrl);
  const loadedVideoUrlRef = useRef(scene?.videoUrl);
  const loadedMediaTypeRef = useRef(scene?.mediaType);
  const loadedProjectionRef = useRef(scene?.projection);

  useEffect(() => {
    if (!threeRef.current || (!scene?.imageUrl && !scene?.videoUrl)) return;
    const isSameScene =
      scene.id === loadedSceneIdRef.current &&
      scene.imageUrl === loadedImageUrlRef.current &&
      scene.videoUrl === loadedVideoUrlRef.current &&
      scene.mediaType === loadedMediaTypeRef.current &&
      scene.projection === loadedProjectionRef.current;

    if (isSameScene) {
      return;
    }
    loadedSceneIdRef.current = scene.id;
    loadedImageUrlRef.current = scene.imageUrl;
    loadedVideoUrlRef.current = scene.videoUrl;
    loadedMediaTypeRef.current = scene.mediaType;
    loadedProjectionRef.current = scene.projection;
    loadPanoramaScene(scene);
  }, [
    scene,
    scene?.id,
    scene?.imageUrl,
    scene?.videoUrl,
    scene?.mediaType,
    scene?.projection,
    transitionKey,
    loadPanoramaScene,
  ]);

  useEffect(() => {
    if (editorMode) {
      if (activeVideoRef.current && !activeVideoRef.current.isPaused()) {
        wasPlayingBeforePlacementRef.current = true;
        activeVideoRef.current.pause();
        setIsPlaying(false);
      }
    } else if (wasPlayingBeforePlacementRef.current) {
      wasPlayingBeforePlacementRef.current = false;
      if (activeVideoRef.current) {
        activeVideoRef.current.play().then(ok => {
          if (ok) setIsPlaying(true);
        });
      }
    }
  }, [editorMode]);

  useEffect(() => {
    const displayed = displayedSceneRef.current;
    if (!threeRef.current || !scene || !displayed) return;
    if (scene.id !== displayed.id) return;
    displayedSceneRef.current = scene;
    rebuildHotspots(scene);
  }, [scene, scene?.hotspots, rebuildHotspots]);

  useEffect(() => {
    const ctx = threeRef.current;
    if (!ctx || !scene) return;
    const yaw = sceneYawRad(scene);
    if (ctx.activeMesh.userData.sceneId === scene.id) ctx.activeMesh.rotation.y = yaw;
    if (ctx.idleMesh.userData.sceneId === scene.id) ctx.idleMesh.rotation.y = yaw;
    if (displayedSceneRef.current?.id === scene.id) ctx.hotspotRoot.rotation.y = yaw;
    ctx.requestRender?.();
  }, [scene, scene?.defaultView?.yaw, scene?.id]);

  useEffect(() => {
    if (autoRotate) {
      threeRef.current?.requestRender?.();
    }
  }, [autoRotate, autoRotateSpeed]);

  const previewedViewRef = useRef({
    sceneId: scene?.id,
    pitch: scene?.defaultView?.pitch,
    fov: scene?.defaultView?.fov,
  });

  useEffect(() => {
    const pitch = scene?.defaultView?.pitch;
    const fov = scene?.defaultView?.fov;
    const prev = previewedViewRef.current;
    previewedViewRef.current = { sceneId: scene?.id, pitch, fov };
    // Only re-snap when the settings of the same scene change, so switching scenes keeps its own look behavior.
    if (!scene || prev.sceneId !== scene.id) return;
    if (prev.pitch === pitch && prev.fov === fov) return;
    if (!threeRef.current || displayedSceneRef.current?.id !== scene.id) return;
    snapToDefaultLook(scene);
  }, [scene, scene?.id, scene?.defaultView?.pitch, scene?.defaultView?.fov, snapToDefaultLook]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvasReady || !canvas) return undefined;

    const applyDrag = (dx, dy, dt) => {
      const ctx = threeRef.current;
      const { w, h } = cssSizeRef.current;
      if (!ctx || w < 1 || h < 1) return;
      const fovRad = THREE.MathUtils.degToRad(ctx.camera.fov);
      const dLon = (dx / w) * fovRad * ctx.camera.aspect;
      const dLat = (dy / h) * fovRad;
      const look = lookRef.current;
      look.lon += dLon;
      look.lat = Math.max(-PITCH_LIMIT, Math.min(PITCH_LIMIT, look.lat + dLat));

      const currentScene = displayedSceneRef.current;
      const is180 = is180Scene(currentScene);
      let hitBoundary = false;
      let hitLatBoundary = false;
      if (is180) {
        const centerOffset = -sceneYawRad(currentScene);
        const clamped180 = clampLook180(look.lon, look.lat, ctx.camera, centerOffset, {
          mode: "drag",
        });
        look.lat = clamped180.lat;
        hitLatBoundary = clamped180.clampedLat;
        const { min, max } = get180CameraYawRange(ctx.camera, centerOffset, look.lat);
        if (look.lon <= min) {
          look.lon = min;
          hitBoundary = true;
        } else if (look.lon >= max) {
          look.lon = max;
          hitBoundary = true;
        }
      }

      if (dt > 0) {
        const instVelLon = hitBoundary
          ? 0
          : Math.max(-MAX_LOOK_VEL, Math.min(MAX_LOOK_VEL, dLon / dt));
        const instVelLat = hitLatBoundary
          ? 0
          : Math.max(-MAX_LOOK_VEL, Math.min(MAX_LOOK_VEL, dLat / dt));
        look.velLon = hitBoundary ? 0 : look.velLon * 0.4 + instVelLon * 0.6;
        look.velLat = hitLatBoundary ? 0 : look.velLat * 0.4 + instVelLat * 0.6;
      }
    };

    const pointerCount = () => pointersRef.current.size;

    const idleCursor = editorMode ? "crosshair" : "grab";

    const hoverCursor = sprite => {
      if (hotspotDragRef.current?.isDragging) return "move";
      const hotspot = sprite?.userData?.hotspot;
      if (!hotspot) return idleCursor;
      if (hotspotInteractionRef.current === "edit") {
        const isSelected = Boolean(
          selectedHotspotIdRef.current && hotspot.id === selectedHotspotIdRef.current
        );
        return isSelected ? "move" : "pointer";
      }
      if (!isTextHotspot(hotspot)) return "pointer";
      // Text pins without a link are only actionable in the editor (opens the edit panel).
      const editable = Boolean(onSphereClickRef.current);
      return hotspot.webUrl?.trim() || editable ? "pointer" : idleCursor;
    };

    const onPointerDown = e => {
      if (e.button === 2) {
        if (clickTimerRef.current) {
          clearTimeout(clickTimerRef.current);
          clickTimerRef.current = null;
        }
        lastClickRef.current = { hotspotId: null, time: 0, x: 0, y: 0 };
        if (hotspotDragRef.current) {
          e.preventDefault();
          e.stopPropagation();
          cancelledByRightClickRef.current = true;
          const { sprite, originalCoords, isDragging } = hotspotDragRef.current;
          if (isDragging && sprite && originalCoords) {
            const pos = yawPitchToVector3(
              originalCoords.yaw,
              originalCoords.pitch,
              PANORAMA_RADIUS - 2
            );
            sprite.position.copy(pos);
          }
          hotspotDragRef.current = null;
          pointerMetaRef.current.didDrag = false;
          pointerMetaRef.current.dragId = null;
          canvas.classList.remove(styles.dragging);
          canvas.style.cursor = hoverCursor(hoveredSpriteRef.current);
          threeRef.current?.requestRender?.();
          return;
        }
      }

      if (cssSizeRef.current.w < 2 || cssSizeRef.current.h < 2) {
        const rect = canvas.getBoundingClientRect();
        cssSizeRef.current = {
          w: Math.max(rect.width, 1),
          h: Math.max(rect.height, 1),
          left: rect.left,
          top: rect.top,
        };
      }
      const look = lookRef.current;
      const meta = pointerMetaRef.current;
      look.velLon = 0;
      look.velLat = 0;
      pointersRef.current.set(e.pointerId, {
        x: e.clientX,
        y: e.clientY,
        startX: e.clientX,
        startY: e.clientY,
      });
      try {
        canvas.setPointerCapture(e.pointerId);
      } catch {
        /* capture optional */
      }

      if (pointerCount() === 1) {
        meta.dragId = e.pointerId;
        meta.didDrag = false;
        meta.lastMoveAt = performance.now();

        if (e.button === 0 && hotspotInteractionRef.current === "edit") {
          const hit = raycastHotspot(e.clientX, e.clientY);
          const hotspot = hit?.userData?.hotspot;
          const isSelected = Boolean(
            hotspot &&
            selectedHotspotIdRef.current &&
            hotspot.id === selectedHotspotIdRef.current
          );
          if (isSelected) {
            hotspotDragRef.current = {
              hotspot,
              sprite: hit,
              originalCoords: { yaw: hotspot.yaw, pitch: hotspot.pitch },
              current: { yaw: hotspot.yaw, pitch: hotspot.pitch },
              startX: e.clientX,
              startY: e.clientY,
              isDragging: false,
            };
          }
        }

        canvas.classList.add(styles.dragging);
      } else if (pointerCount() === 2) {
        if (hotspotDragRef.current) {
          const { sprite, originalCoords, isDragging } = hotspotDragRef.current;
          if (isDragging && sprite && originalCoords) {
            const pos = yawPitchToVector3(
              originalCoords.yaw,
              originalCoords.pitch,
              PANORAMA_RADIUS - 2
            );
            sprite.position.copy(pos);
          }
          hotspotDragRef.current = null;
        }
        const pts = [...pointersRef.current.values()];
        meta.pinch = {
          startDist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1,
          startFov: threeRef.current?.camera.fov ?? DEFAULT_FOV,
        };
        meta.didPinch = true;
        meta.dragId = null;
        setHoveredSprite(null);
      }
      threeRef.current?.requestRender?.();
    };

    const onPointerMove = e => {
      const ptr = pointersRef.current.get(e.pointerId);
      if (!ptr) {
        const meta = pointerMetaRef.current;
        if (meta.dragId != null || meta.pinch) return;
        const t = performance.now();
        if (t - meta.hoverAt < HOVER_THROTTLE_MS) return;
        meta.hoverAt = t;
        const hit = raycastHotspot(e.clientX, e.clientY);
        setHoveredSprite(hit);
        canvas.style.cursor = hoverCursor(hit);
        return;
      }

      const prevX = ptr.x;
      const prevY = ptr.y;
      ptr.x = e.clientX;
      ptr.y = e.clientY;
      const meta = pointerMetaRef.current;
      const now = performance.now();

      if (meta.pinch && pointerCount() >= 2) {
        const pts = [...pointersRef.current.values()];
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y) || 1;
        const ctx = threeRef.current;
        if (ctx) {
          const currentScene = displayedSceneRef.current;
          const is180 = is180Scene(currentScene);
          const maxFov = is180
            ? Math.min(MAX_FOV, getMaxFovFor180(ctx.camera.aspect))
            : MAX_FOV;
          ctx.camera.fov = Math.max(
            MIN_FOV,
            Math.min(maxFov, meta.pinch.startFov * (meta.pinch.startDist / dist))
          );
          ctx.camera.updateProjectionMatrix();
          if (is180) {
            const centerOffset = -sceneYawRad(currentScene);
            apply180StaticCameraBounds(ctx.camera, centerOffset);
          }
        }
        lookRef.current.velLon = 0;
        lookRef.current.velLat = 0;
        ctx?.requestRender?.();
        return;
      }

      if (meta.dragId === e.pointerId) {
        const dt = Math.max(0.001, (now - meta.lastMoveAt) / 1000);
        meta.lastMoveAt = now;

        if (hotspotDragRef.current) {
          const drag = hotspotDragRef.current;
          const dist = Math.hypot(e.clientX - drag.startX, e.clientY - drag.startY);
          if (!drag.isDragging && dist > DRAG_THRESHOLD_PX) {
            drag.isDragging = true;
            meta.didDrag = true;
            if (activeVideoRef.current && !activeVideoRef.current.isPaused()) {
              wasPlayingBeforeDragRef.current = true;
              activeVideoRef.current.pause();
              setIsPlaying(false);
            }
            setHoveredSprite(null);
            canvas.style.cursor = "move";
          }

          if (drag.isDragging) {
            const coords = raycastSphereYawPitch(e.clientX, e.clientY);
            if (coords) {
              drag.current = coords;
              const pos = yawPitchToVector3(coords.yaw, coords.pitch, PANORAMA_RADIUS - 2);
              drag.sprite.position.copy(pos);
              threeRef.current?.requestRender?.();
            }
            canvas.style.cursor = "move";
            return;
          }
          return;
        }

        const distFromStart = Math.hypot(e.clientX - ptr.startX, e.clientY - ptr.startY);
        if (!meta.didDrag && distFromStart > DRAG_THRESHOLD_PX) {
          if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
          }
          meta.didDrag = true;
          setHoveredSprite(null);
          canvas.style.cursor = "grabbing";
          const initDx = e.clientX - ptr.startX;
          const initDy = e.clientY - ptr.startY;
          lastClickRef.current = { hotspotId: null, time: 0, x: 0, y: 0 };
          applyDrag(initDx, initDy, dt);
          threeRef.current?.requestRender?.();
          return;
        }

        if (meta.didDrag) {
          if (clickTimerRef.current) {
            clearTimeout(clickTimerRef.current);
            clickTimerRef.current = null;
          }
          const dx = e.clientX - prevX;
          const dy = e.clientY - prevY;
          lastClickRef.current = { hotspotId: null, time: 0, x: 0, y: 0 };
          applyDrag(dx, dy, dt);
          threeRef.current?.requestRender?.();
        }
      }
    };

    const finishPointer = (e, { asCancel } = {}) => {
      const meta = pointerMetaRef.current;
      const had = pointersRef.current.delete(e.pointerId);
      if (!had && meta.dragId !== e.pointerId) return;

      try {
        canvas.releasePointerCapture(e.pointerId);
      } catch {
        /* released */
      }

      if (pointerCount() < 2) meta.pinch = null;
      if (meta.dragId === e.pointerId) meta.dragId = null;

      if (pointerCount() === 1 && !meta.pinch) {
        const remainingId = pointersRef.current.keys().next().value;
        meta.dragId = remainingId;
        meta.lastMoveAt = performance.now();
      }

      if (pointerCount() > 0) return;

      if (e.button === 2) {
        if (hotspotDragRef.current) {
          const { sprite, originalCoords, isDragging } = hotspotDragRef.current;
          if (isDragging && sprite && originalCoords) {
            const pos = yawPitchToVector3(
              originalCoords.yaw,
              originalCoords.pitch,
              PANORAMA_RADIUS - 2
            );
            sprite.position.copy(pos);
          }
          hotspotDragRef.current = null;
        }
        return;
      }

      const activeDrag = hotspotDragRef.current;
      hotspotDragRef.current = null;

      if (wasPlayingBeforeDragRef.current) {
        wasPlayingBeforeDragRef.current = false;
        if (activeVideoRef.current) {
          activeVideoRef.current.play().then(ok => {
            if (ok) setIsPlaying(true);
          });
        }
      }

      if (activeDrag?.isDragging) {
        if (!asCancel) {
          const coords =
            raycastSphereYawPitch(e.clientX, e.clientY) || activeDrag.current;
          const targetHotspot = activeDrag.hotspot;
          if (coords && onHotspotMoveRef.current) {
            onHotspotMoveRef.current(targetHotspot, coords);
          }
        } else {
          const { sprite, originalCoords } = activeDrag;
          if (sprite && originalCoords) {
            const pos = yawPitchToVector3(
              originalCoords.yaw,
              originalCoords.pitch,
              PANORAMA_RADIUS - 2
            );
            sprite.position.copy(pos);
          }
        }
        meta.didDrag = false;
        meta.didPinch = false;
        meta.dragId = null;
        meta.pinch = null;
        canvas.classList.remove(styles.dragging);
        canvas.style.cursor = hoverCursor(hoveredSpriteRef.current);
        threeRef.current?.requestRender?.();
        scheduleWarmRef.current?.();
        return;
      }

      const suppressClick =
        asCancel || meta.didDrag || meta.didPinch || e.button !== 0;

      if (!suppressClick) {
        const displayedId = displayedSceneRef.current?.id;
        const hit = raycastHotspot(e.clientX, e.clientY);
        const hotspot = hit?.userData?.hotspot;
        const isText = isTextHotspot(hotspot);

        if (hotspotInteractionRef.current === "edit") {
          if (hotspot) {
            const now = performance.now();
            const last = lastClickRef.current;
            const isSameHotspot = last.hotspotId === hotspot.id;
            const isWithinTime = now - last.time <= DOUBLE_CLICK_DELAY_MS;
            const isWithinDist =
              Math.hypot(e.clientX - last.x, e.clientY - last.y) <=
              DOUBLE_CLICK_DIST_PX;

            if (isSameHotspot && isWithinTime && isWithinDist) {
              if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
              }
              lastClickRef.current = { hotspotId: null, time: 0, x: 0, y: 0 };
              if (onHotspotDoubleClickRef.current) {
                onHotspotDoubleClickRef.current(hotspot);
              } else if (
                hotspot &&
                !isText &&
                hotspot.targetSceneId &&
                hotspot.targetSceneId !== displayedId
              ) {
                onSceneChangeRef.current?.(hotspot.targetSceneId);
              } else if (onHotspotClickRef.current) {
                onHotspotClickRef.current(hotspot);
              }
            } else {
              if (clickTimerRef.current) {
                clearTimeout(clickTimerRef.current);
                clickTimerRef.current = null;
              }
              lastClickRef.current = {
                hotspotId: hotspot.id,
                time: now,
                x: e.clientX,
                y: e.clientY,
              };
              clickTimerRef.current = setTimeout(() => {
                clickTimerRef.current = null;
                lastClickRef.current = { hotspotId: null, time: 0, x: 0, y: 0 };
                if (!mountedRef.current) return;
                onHotspotClickRef.current?.(hotspot);
              }, DOUBLE_CLICK_DELAY_MS);
            }
          } else if (onSphereClickRef.current) {
            if (clickTimerRef.current) {
              clearTimeout(clickTimerRef.current);
              clickTimerRef.current = null;
            }
            lastClickRef.current = { hotspotId: null, time: 0, x: 0, y: 0 };
            const coords = raycastSphereYawPitch(e.clientX, e.clientY);
            if (coords) {
              onSphereClickRef.current(coords);
            }
          }
        } else {
          if (
            hotspot &&
            !isText &&
            hotspot.targetSceneId &&
            hotspot.targetSceneId !== displayedId
          ) {
            onSceneChangeRef.current?.(hotspot.targetSceneId);
          } else if (hotspot && isText) {
            if (onHotspotClickRef.current) onHotspotClickRef.current(hotspot);
            else openHotspotUrl(hotspot);
          } else if (hotspot && editorMode) {
            onHotspotClickRef.current?.(hotspot);
          } else if (!hit && onSphereClickRef.current) {
            const coords = raycastSphereYawPitch(e.clientX, e.clientY);
            if (coords) {
              onSphereClickRef.current(coords);
            }
          } else if (hotspot && !editorMode) {
            onHotspotClickRef.current?.(hotspot);
          }
        }
      } else if (!asCancel && meta.didDrag) {
        const released = velocityOnRelease(
          lookRef.current.velLon,
          lookRef.current.velLat,
          meta.lastMoveAt,
          performance.now()
        );
        lookRef.current.velLon = released.velLon;
        lookRef.current.velLat = released.velLat;
      } else {
        lookRef.current.velLon = 0;
        lookRef.current.velLat = 0;
      }

      meta.didDrag = false;
      meta.didPinch = false;
      meta.dragId = null;
      meta.pinch = null;
      canvas.classList.remove(styles.dragging);
      canvas.style.cursor = hoverCursor(hoveredSpriteRef.current);
      threeRef.current?.requestRender?.();
      scheduleWarmRef.current?.();
    };

    const onPointerUp = e => finishPointer(e, { asCancel: false });
    const onPointerCancel = e => finishPointer(e, { asCancel: true });

    const onKeyDown = e => {
      if (e.key === "Escape") {
        if (hotspotDragRef.current) {
          e.preventDefault();
          e.stopPropagation();
          const { sprite, originalCoords, isDragging } = hotspotDragRef.current;
          if (isDragging && sprite && originalCoords) {
            const pos = yawPitchToVector3(
              originalCoords.yaw,
              originalCoords.pitch,
              PANORAMA_RADIUS - 2
            );
            sprite.position.copy(pos);
          }
          hotspotDragRef.current = null;
          pointerMetaRef.current.didDrag = false;
          pointerMetaRef.current.dragId = null;
          canvas.classList.remove(styles.dragging);
          canvas.style.cursor = hoverCursor(hoveredSpriteRef.current);
          threeRef.current?.requestRender?.();
        }
      }
    };

    const onWheel = e => {
      e.preventDefault();
      const ctx = threeRef.current;
      if (!ctx) return;
      const currentScene = displayedSceneRef.current;
      const is180 = is180Scene(currentScene);
      const maxFov = is180
        ? Math.min(MAX_FOV, getMaxFovFor180(ctx.camera.aspect))
        : MAX_FOV;
      ctx.camera.fov += e.deltaY * 0.04;
      ctx.camera.fov = Math.max(MIN_FOV, Math.min(maxFov, ctx.camera.fov));
      ctx.camera.updateProjectionMatrix();
      if (is180) {
        const centerOffset = -sceneYawRad(currentScene);
        apply180StaticCameraBounds(ctx.camera, centerOffset);
      }
      ctx.requestRender?.();
    };

    const onContextMenu = e => {
      if (cancelledByRightClickRef.current || hotspotDragRef.current) {
        e.preventDefault();
        e.stopPropagation();
        cancelledByRightClickRef.current = false;
        if (hotspotDragRef.current) {
          const { sprite, originalCoords, isDragging } = hotspotDragRef.current;
          if (isDragging && sprite && originalCoords) {
            const pos = yawPitchToVector3(
              originalCoords.yaw,
              originalCoords.pitch,
              PANORAMA_RADIUS - 2
            );
            sprite.position.copy(pos);
          }
          hotspotDragRef.current = null;
          pointerMetaRef.current.didDrag = false;
          pointerMetaRef.current.dragId = null;
          canvas.classList.remove(styles.dragging);
          canvas.style.cursor = hoverCursor(hoveredSpriteRef.current);
          threeRef.current?.requestRender?.();
        }
      }
    };

    canvas.style.cursor = editorMode ? "crosshair" : "grab";
    canvas.addEventListener("pointerdown", onPointerDown);
    canvas.addEventListener("pointermove", onPointerMove);
    canvas.addEventListener("pointerup", onPointerUp);
    canvas.addEventListener("pointercancel", onPointerCancel);
    canvas.addEventListener("lostpointercapture", onPointerCancel);
    canvas.addEventListener("wheel", onWheel, { passive: false });
    canvas.addEventListener("contextmenu", onContextMenu);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      canvas.removeEventListener("pointerdown", onPointerDown);
      canvas.removeEventListener("pointermove", onPointerMove);
      canvas.removeEventListener("pointerup", onPointerUp);
      canvas.removeEventListener("pointercancel", onPointerCancel);
      canvas.removeEventListener("lostpointercapture", onPointerCancel);
      canvas.removeEventListener("wheel", onWheel);
      canvas.removeEventListener("contextmenu", onContextMenu);
      window.removeEventListener("keydown", onKeyDown);
      if (clickTimerRef.current) {
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = null;
      }
    };
  }, [
    apply180StaticCameraBounds,
    canvasReady,
    editorMode,
    getPointerNDC,
    raycastHotspot,
    raycastSphereYawPitch,
    setHoveredSprite,
  ]);

  return (
    <div ref={containerRef} className={styles.viewer}>
      {loadError ? (
        <button type="button" className={styles.retry} onClick={handleRetry}>
          {loadError.isVideo
            ? "Couldn't stream this 360° video. Retry"
            : "Couldn't load this room. Retry"}
        </button>
      ) : null}

      {isVideo && !loadError && (
        <div
          className={styles.videoControls}
          onPointerDown={e => e.stopPropagation()}
          onClick={e => e.stopPropagation()}
        >
          <button
            type="button"
            className={styles.controlButton}
            onClick={handleTogglePlay}
            aria-label={isPlaying ? "Pause 360 video" : "Play 360 video"}
            title={isPlaying ? "Pause" : "Play"}
          >
            {isPlaying ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <rect x="6" y="4" width="4" height="16" rx="1" />
                <rect x="14" y="4" width="4" height="16" rx="1" />
              </svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <button
            type="button"
            className={styles.controlButton}
            onClick={handleToggleMute}
            aria-label={isMuted ? "Unmute 360 video" : "Mute 360 video"}
            title={isMuted ? "Unmute" : "Mute"}
          >
            {isMuted ? (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
                <line x1="23" y1="9" x2="17" y2="15" />
                <line x1="17" y1="9" x2="23" y2="15" />
              </svg>
            ) : (
              <svg
                width="15"
                height="15"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M11 5L6 9H2v6h4l5 4V5z" fill="currentColor" />
                <path d="M19.07 4.93a10 10 0 0 1 0 14.14M15.54 8.46a5 5 0 0 1 0 7.07" />
              </svg>
            )}
          </button>

          {videoRenderDiagnostic !== null && (
            <>
              <button
                type="button"
                className={styles.controlButton}
                onClick={handleToggleVideoRenderDiagnostic}
                aria-label={`Switch to mode ${videoRenderDiagnostic === "normal" ? "B: suppress video rendering" : "A: normal rendering"}`}
                title="Toggle video rendering without restarting playback"
              >
                {videoRenderDiagnostic === "normal" ? "A" : "B"}
              </button>
              <span className={styles.videoBadge}>
                {videoRenderDiagnostic === "normal" ? "Normal rendering" : "Video rendering suppressed"}
              </span>
            </>
          )}

          {isBuffering ? (
            <div className={styles.bufferingBadge}>
              <span className={styles.bufferingSpinner} />
              <span>Buffering</span>
            </div>
          ) : (
            <span className={styles.videoBadge}>360° Video</span>
          )}
        </div>
      )}

      {autoplayBlocked && !loadError && (
        <button
          type="button"
          className={styles.autoplayOverlay}
          onClick={handleTogglePlay}
          aria-label="Tap to play 360 video"
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
            <path d="M8 5v14l11-7z" />
          </svg>
          <span>Play 360° Video</span>
        </button>
      )}
    </div>
  );
});

export default PanoramaEditorViewer;
